import { FormEvent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, Ban, CalendarClock, Copy, Eye, ImagePlus, Loader2, Megaphone, MousePointerClick, Pencil, Plus, RefreshCcw, Save, Send, Trash2 } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog'
import { EmptyState } from '../components/ui/empty-state'
import { MediaPickerDialog } from '../components/media/MediaPickerDialog'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { useToast } from '../components/ui/toast'
import { useAuth } from '../hooks/useAuth'
import {
  cancelCampaignMessage,
  CampaignAudienceCohort,
  CampaignAudienceGroup,
  CampaignDisplayMode,
  CampaignMessage,
  CampaignMessageBlock,
  CampaignPollOption,
  CampaignPollQuestion,
  CampaignPollBranch,
  CampaignMessageStatus,
  createCampaignMessage,
  duplicateCampaignMessage,
  CampaignDeepLinkTarget,
  getCampaignMessages,
  getCampaignAudienceOptions,
  getDeepLinkTargets,
  SaveCampaignMessageRequest,
  scheduleCampaignMessage,
  sendCampaignMessage,
  updateCampaignMessage
} from '../lib/api/campaignMessages'
import { MediaAsset } from '../lib/api/mediaAssets'
import { cn } from '../utils/cn'

const COHORT_OPTIONS: { value: CampaignAudienceCohort; label: string }[] = [
  { value: 'All', label: 'All users' },
  { value: 'Free', label: 'Free users' },
  { value: 'Paid', label: 'Paid users' },
  { value: 'NewUsers', label: 'New users' },
  { value: 'ActiveUsers', label: 'Active users' },
  { value: 'UpcomingGoalHike', label: 'Users with an upcoming Goal Hike' },
  { value: 'GroupMembers', label: 'Members of a specific group' },
  { value: 'TourOperators', label: 'Tour Operators' }
]

const MAX_BLOCKS = 20
const newPollOption = (): CampaignPollOption => ({
  id: globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2),
  text: ''
})
const newPollQuestion = (): CampaignPollQuestion => ({
  id: globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2),
  question: '', allowsMultiple: false, options: [newPollOption(), newPollOption()]
})

type CampaignFormState = {
  title: string
  blocks: CampaignMessageBlock[]
  audienceCohort: CampaignAudienceCohort
  targetGroupId: string
  audienceWindowDays: number
  requireMarketingOptIn: boolean
  expiresAtLocal: string
  displayMode: CampaignDisplayMode
  canDismiss: boolean
  requiresAcknowledgement: boolean
  pollEnabled: boolean
  pollQuestions: CampaignPollQuestion[]
  pollBranch: { questionId: string; routes: { optionId: string; followUpQuestionId?: string | null }[] } | null
  pollClosesAtLocal: string
}

const initialForm: CampaignFormState = {
  title: '',
  blocks: [{ kind: 'Paragraph', text: '' }],
  audienceCohort: 'All',
  targetGroupId: '',
  audienceWindowDays: 30,
  requireMarketingOptIn: false,
  expiresAtLocal: '',
  displayMode: 'Persistent',
  canDismiss: true,
  requiresAcknowledgement: false,
  pollEnabled: false,
  pollQuestions: [],
  pollBranch: null,
  pollClosesAtLocal: '',
}

const emptyBlockFor = (kind: CampaignMessageBlock['kind']): CampaignMessageBlock => {
  if (kind === 'Paragraph') return { kind, text: '' }
  // For Button blocks, `text` holds the {id} segment while editing a detail deep link.
  if (kind === 'Button') return { kind, label: '', deepLink: '', text: '' }
  return { kind, imageUrl: null }
}

// Detail deep links such as /post/{id} become concrete links once the admin supplies an id.
const buildDeepLink = (path: string, id: string) => path.replace('{id}', encodeURIComponent(id.trim()))

const countImages = (blocks: CampaignMessageBlock[]) =>
  blocks.filter((block) => block.kind === 'Image').length

const STATUS_BADGE_CLASSES: Record<CampaignMessageStatus, string> = {
  Draft: 'bg-yellow-100 text-yellow-800',
  Scheduled: 'bg-purple-100 text-purple-800',
  Sending: 'bg-blue-100 text-blue-800',
  Sent: 'bg-green-100 text-green-800',
  Canceled: 'bg-gray-100 text-gray-700',
  Failed: 'bg-red-100 text-red-800'
}

const formatDateTime = (value?: string | null) => {
  if (!value) return '—'
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString()
}

const describeAudience = (cohort: CampaignAudienceCohort, requireMarketingOptIn: boolean) => {
  const audience = COHORT_OPTIONS.find((option) => option.value === cohort)?.label.toLowerCase() ?? cohort
  return requireMarketingOptIn ? `${audience} who opted in to marketing` : audience
}

const displayStatus = (campaign: CampaignMessage): string => {
  if (campaign.status === 'Sent' && campaign.expiresAtUtc && new Date(campaign.expiresAtUtc) <= new Date()) return 'Expired'
  if (campaign.status === 'Sent') return 'Live'
  if (campaign.status === 'Canceled') return 'Disabled'
  return campaign.status
}

type ButtonBlockFieldsProps = {
  index: number
  block: CampaignMessageBlock
  targets: CampaignDeepLinkTarget[]
  onChange: (patch: Partial<CampaignMessageBlock>) => void
}

const ButtonBlockFields = ({ index, block, targets, onChange }: ButtonBlockFieldsProps) => {
  const path = block.deepLink ?? ''
  const isExternal = /^https:\/\//i.test(path)
  const idSegment = (block.text ?? '').trim()
  const needsId = path.includes('{id}')
  const resolvedPath = needsId ? (idSegment ? buildDeepLink(path, idSegment) : null) : path || null

  return (
    <div className="space-y-2">
      <Input
        aria-label={`Button ${index + 1} label`}
        maxLength={40}
        value={block.label ?? ''}
        onChange={(event) => onChange({ label: event.target.value })}
        placeholder="Call to action, e.g. Read the trip report"
      />
      <select
        aria-label={`Button ${index + 1} target`}
        value={isExternal ? '__external__' : path}
        onChange={(event) => onChange({ deepLink: event.target.value === '__external__' ? 'https://' : event.target.value })}
        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
      >
        <option value="">Choose a deep link target…</option>
        <option value="__external__">External HTTPS URL</option>
        {targets.map((target) => (
          <option key={target.path} value={target.path}>
            {target.description} ({target.path})
          </option>
        ))}
      </select>
      {isExternal ? (
        <Input
          aria-label={`Button ${index + 1} external URL`}
          value={path}
          onChange={(event) => onChange({ deepLink: event.target.value })}
          placeholder="https://example.com/page"
        />
      ) : needsId ? (
        <Input
          aria-label={`Button ${index + 1} target id`}
          value={block.text ?? ''}
          onChange={(event) => onChange({ text: event.target.value })}
          placeholder="Post, group, or hike id"
        />
      ) : null}
      {resolvedPath ? (
        <p className="text-xs text-muted-foreground">Opens: {resolvedPath}</p>
      ) : null}
    </div>
  )
}

export const CampaignMessagesPage = () => {
  const { user } = useAuth()
  const { addToast } = useToast()
  const [campaigns, setCampaigns] = useState<CampaignMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [pickerBlockIndex, setPickerBlockIndex] = useState<number | null>(null)
  const [actingId, setActingId] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<CampaignFormState>(initialForm)
  const [previewBranchOptionId, setPreviewBranchOptionId] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [schedulingId, setSchedulingId] = useState<string | null>(null)
  const [scheduledForLocal, setScheduledForLocal] = useState('')
  const [scheduleError, setScheduleError] = useState<string | null>(null)
  const [scheduling, setScheduling] = useState(false)
  const [deepLinkTargets, setDeepLinkTargets] = useState<CampaignDeepLinkTarget[]>([])
  const [audienceGroups, setAudienceGroups] = useState<CampaignAudienceGroup[]>([])

  const role = (user?.role ?? 'Guest').toString()
  const isEditor = role === 'Admin' || role === 'Editor'
  const hasButtonBlock = form.blocks.some((block) => block.kind === 'Button')
  const branchSourceIndex = form.pollBranch ? form.pollQuestions.findIndex((question) => question.id === form.pollBranch?.questionId) : -1
  const branchSource = branchSourceIndex >= 0 ? form.pollQuestions[branchSourceIndex] : null
  const previewRoute = form.pollBranch?.routes.find((route) => route.optionId === previewBranchOptionId)
  const previewFollowUp = form.pollQuestions.find((question) => question.id === previewRoute?.followUpQuestionId)
  const previewQuestions = branchSourceIndex >= 0
    ? [...form.pollQuestions.slice(0, branchSourceIndex + 1), ...(previewFollowUp ? [previewFollowUp] : [])]
    : form.pollQuestions

  const loadCampaigns = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    try {
      const data = await getCampaignMessages()
      setCampaigns(data)
    } catch (error) {
      console.error('Failed to load campaign messages', error)
      addToast({ title: 'Error', description: 'Could not load campaign messages.' })
    } finally {
      if (!silent) setLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    if (!isEditor) {
      setLoading(false)
      return
    }

    void loadCampaigns()
  }, [isEditor, loadCampaigns])

  // Fetch the allowed button deep-link targets once per editor session.
  useEffect(() => {
    if (!isEditor) return

    let cancelled = false
    getDeepLinkTargets()
      .then((data) => {
        if (!cancelled) setDeepLinkTargets(data.targets)
      })
      .catch((error) => {
        console.error('Failed to load deep link targets', error)
      })

    return () => {
      cancelled = true
    }
  }, [isEditor])

  useEffect(() => {
    if (!isEditor) return
    getCampaignAudienceOptions()
      .then((data) => setAudienceGroups(data.groups))
      .catch((error) => console.error('Failed to load campaign audience options', error))
  }, [isEditor])

  // While anything is sending or scheduled, poll so delivery counts and status changes update live.
  const hasLiveCampaign = useMemo(
    () => campaigns.some((campaign) => campaign.status === 'Sending' || campaign.status === 'Scheduled'),
    [campaigns]
  )

  useEffect(() => {
    if (!isEditor || !hasLiveCampaign) return

    const timer = window.setInterval(() => {
      void loadCampaigns({ silent: true })
    }, 5000)

    return () => window.clearInterval(timer)
  }, [hasLiveCampaign, isEditor, loadCampaigns])

  const resetForm = useCallback(() => {
    setEditingId(null)
    setForm(initialForm)
    setPreviewBranchOptionId('')
    setFormError(null)
  }, [])

  const startEdit = (campaign: CampaignMessage) => {
    if (campaign.status !== 'Draft') return
    setEditingId(campaign.id)
    setPreviewBranchOptionId('')
    // Legacy drafts have no stored blocks, so seed one paragraph block from the plain-text body.
    const blocks: CampaignMessageBlock[] = campaign.blocks.length > 0
      ? campaign.blocks
      : [{ kind: 'Paragraph', text: campaign.content }]
    setForm({
      title: campaign.title,
      blocks,
      audienceCohort: campaign.audienceCohort,
      targetGroupId: campaign.targetGroupId ?? '',
      audienceWindowDays: campaign.audienceWindowDays ?? 30,
      requireMarketingOptIn: campaign.requireMarketingOptIn,
      expiresAtLocal: campaign.expiresAtUtc ? toLocalInputValue(campaign.expiresAtUtc) : '',
      displayMode: campaign.displayMode ?? 'Persistent',
      canDismiss: campaign.canDismiss ?? true,
      requiresAcknowledgement: campaign.requiresAcknowledgement ?? false,
      pollEnabled: Boolean(campaign.poll),
      pollQuestions: campaign.poll ? campaign.poll.questions?.length ? campaign.poll.questions : [{
        id: newPollQuestion().id, question: campaign.poll.question,
        allowsMultiple: campaign.poll.allowsMultiple, options: campaign.poll.options
      }] : [],
      pollBranch: campaign.poll?.branch ?? null,
      pollClosesAtLocal: campaign.poll ? toLocalInputValue(campaign.poll.closesAtUtc) : '',
    })
    setFormError(null)
  }

  const updateBlock = (index: number, patch: Partial<CampaignMessageBlock>) => {
    setForm((prev) => ({
      ...prev,
      blocks: prev.blocks.map((block, i) => (i === index ? { ...block, ...patch } : block))
    }))
  }

  const addBlock = (kind: CampaignMessageBlock['kind']) => {
    setForm((prev) => ({ ...prev, blocks: [...prev.blocks, emptyBlockFor(kind)] }))
  }

  const removeBlock = (index: number) => {
    setForm((prev) => ({ ...prev, blocks: prev.blocks.filter((_, i) => i !== index) }))
  }

  const moveBlock = (index: number, direction: -1 | 1) => {
    setForm((prev) => {
      const next = [...prev.blocks]
      const target = index + direction
      if (target < 0 || target >= next.length) return prev
      ;[next[index], next[target]] = [next[target], next[index]]
      return { ...prev, blocks: next }
    })
  }

  // Blocks keep storing plain image URLs; the media library only supplies them.
  const handleMediaAssetSelected = (index: number, asset: MediaAsset) => {
    updateBlock(index, { imageUrl: asset.url })
  }

  const setBranchRoute = (optionId: string, next: 'unassigned' | 'end' | 'follow-up') => {
    setForm((prev) => {
      if (!prev.pollBranch) return prev
      const previous = prev.pollBranch.routes.find((route) => route.optionId === optionId)
      let pollQuestions = prev.pollQuestions
      let followUpQuestionId: string | null | undefined
      if (next === 'follow-up') {
        if (previous?.followUpQuestionId) followUpQuestionId = previous.followUpQuestionId
        else {
          const followUp = newPollQuestion()
          followUpQuestionId = followUp.id
          pollQuestions = [...pollQuestions, followUp]
        }
      } else {
        followUpQuestionId = next === 'end' ? null : undefined
        if (previous?.followUpQuestionId) {
          pollQuestions = pollQuestions.filter((question) => question.id !== previous.followUpQuestionId)
        }
      }
      return {
        ...prev, pollQuestions,
        pollBranch: {
          ...prev.pollBranch,
          routes: prev.pollBranch.routes.map((route) => route.optionId === optionId ? { ...route, followUpQuestionId } : route)
        }
      }
    })
  }

  const renderPollQuestionEditor = (question: CampaignPollQuestion, questionIndex: number, followUpLabel?: string): ReactNode => {
    const isBranchSource = form.pollBranch?.questionId === question.id
    return (
      <div key={question.id} className="space-y-3 rounded-md border border-border p-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold">{followUpLabel ?? `Page ${questionIndex + 1}`}</p>
          {!followUpLabel && !isBranchSource && form.pollQuestions.length > 1 ? (
            <Button type="button" variant="outline" size="sm" onClick={() => setForm((prev) => ({ ...prev, pollQuestions: prev.pollQuestions.filter((item) => item.id !== question.id) }))}>Remove page {questionIndex + 1}</Button>
          ) : null}
        </div>
        <div className="space-y-1">
          <label htmlFor={`campaign-poll-question-${question.id}`} className="text-sm font-medium">{followUpLabel ? `${followUpLabel} question` : questionIndex === 0 ? 'Question' : `Question ${questionIndex + 1}`}</label>
          <Textarea id={`campaign-poll-question-${question.id}`} maxLength={500} value={question.question} onChange={(event) => setForm((prev) => ({ ...prev, pollQuestions: prev.pollQuestions.map((item) => item.id === question.id ? { ...item, question: event.target.value } : item) }))} />
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={question.allowsMultiple} disabled={isBranchSource} onChange={(event) => setForm((prev) => ({ ...prev, pollQuestions: prev.pollQuestions.map((item) => item.id === question.id ? { ...item, allowsMultiple: event.target.checked } : item) }))} /> Allow multiple choices{followUpLabel ? ` ${followUpLabel.toLowerCase()}` : questionIndex > 0 ? ` on page ${questionIndex + 1}` : ''}</label>
        <p className="text-sm font-medium">Answer options</p>
        {question.options.map((option, index) => (
          <div key={option.id} className="flex gap-2">
            <Input aria-label={`Poll option ${index + 1}${followUpLabel ? ` ${followUpLabel.toLowerCase()}` : questionIndex > 0 ? ` on page ${questionIndex + 1}` : ''}`} maxLength={200} value={option.text} onChange={(event) => setForm((prev) => ({ ...prev, pollQuestions: prev.pollQuestions.map((item) => item.id === question.id ? { ...item, options: item.options.map((choice) => choice.id === option.id ? { ...choice, text: event.target.value } : choice) } : item) }))} />
            <Button type="button" variant="outline" size="sm" disabled={question.options.length <= 2} onClick={() => setForm((prev) => {
              let pollQuestions = prev.pollQuestions.map((item) => item.id === question.id ? { ...item, options: item.options.filter((choice) => choice.id !== option.id) } : item)
              const route = prev.pollBranch?.questionId === question.id ? prev.pollBranch.routes.find((item) => item.optionId === option.id) : null
              if (route?.followUpQuestionId) pollQuestions = pollQuestions.filter((item) => item.id !== route.followUpQuestionId)
              return { ...prev, pollQuestions, pollBranch: route && prev.pollBranch ? { ...prev.pollBranch, routes: prev.pollBranch.routes.filter((item) => item.optionId !== option.id) } : prev.pollBranch }
            })}>Remove</Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" disabled={question.options.length >= 10} onClick={() => setForm((prev) => {
          const option = newPollOption()
          return {
            ...prev,
            pollQuestions: prev.pollQuestions.map((item) => item.id === question.id ? { ...item, options: [...item.options, option] } : item),
            pollBranch: prev.pollBranch?.questionId === question.id ? { ...prev.pollBranch, routes: [...prev.pollBranch.routes, { optionId: option.id, followUpQuestionId: undefined }] } : prev.pollBranch
          }
        })}>Add option{followUpLabel ? ` ${followUpLabel.toLowerCase()}` : questionIndex > 0 ? ` to page ${questionIndex + 1}` : ''}</Button>
        {isBranchSource ? (
          <div className="space-y-3 border-t border-border pt-3">
            <p className="text-sm font-medium">Next step for each answer</p>
            {question.options.map((option, index) => {
              const route = form.pollBranch?.routes.find((item) => item.optionId === option.id)
              const routeValue = route?.followUpQuestionId === undefined ? 'unassigned' : route.followUpQuestionId === null ? 'end' : 'follow-up'
              const followUp = form.pollQuestions.find((item) => item.id === route?.followUpQuestionId)
              const label = `Follow-up after ${option.text.trim() || `option ${index + 1}`}`
              return (
                <div key={option.id} className="space-y-2 rounded-md bg-muted/40 p-3">
                  <label className="block text-sm font-medium" htmlFor={`campaign-poll-route-${option.id}`}>After {option.text.trim() || `option ${index + 1}`}</label>
                  <select id={`campaign-poll-route-${option.id}`} value={routeValue} onChange={(event) => setBranchRoute(option.id, event.target.value as 'unassigned' | 'end' | 'follow-up')} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                    <option value="unassigned">Choose next step</option>
                    <option value="end">End poll</option>
                    <option value="follow-up" disabled={form.pollQuestions.length >= 10 && !followUp}>{followUp ? 'Follow-up question' : 'Add follow-up question'}</option>
                  </select>
                  {followUp ? renderPollQuestionEditor(followUp, form.pollQuestions.indexOf(followUp), label) : null}
                </div>
              )
            })}
          </div>
        ) : null}
      </div>
    )
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const title = form.title.trim()

    if (!title) {
      setFormError('Title and content are required.')
      return
    }

    if (form.blocks.some((block) => block.kind === 'Image' && !block.imageUrl)) {
      setFormError('Upload an image for each image block before saving.')
      return
    }

    if (form.audienceCohort === 'GroupMembers' && !form.targetGroupId) {
      setFormError('Choose the group whose members should receive this campaign.')
      return
    }

    if (
      form.blocks.some(
        (block) =>
          block.kind === 'Button' &&
          (!(block.label ?? '').trim() ||
            !(block.deepLink ?? '').trim() ||
            ((block.deepLink ?? '').includes('{id}') && !(block.text ?? '').trim()))
      )
    ) {
      setFormError('Every button needs a label and a target.')
      return
    }

    // Trim paragraph text client-side and drop empty blocks so the payload matches the DTO rules.
    const blocks: CampaignMessageBlock[] = []
    for (const block of form.blocks) {
      if (block.kind === 'Paragraph') {
        const text = (block.text ?? '').trim()
        if (text) blocks.push({ kind: 'Paragraph', text })
      } else if (block.kind === 'Button') {
        const path = (block.deepLink ?? '').trim()
        if (path) {
          blocks.push({
            kind: 'Button',
            label: (block.label ?? '').trim(),
            deepLink: buildDeepLink(path, (block.text ?? '').trim())
          })
        }
      } else if (block.imageUrl) {
        const caption = (block.caption ?? '').trim()
        blocks.push(
          caption
            ? { kind: 'Image', imageUrl: block.imageUrl, caption }
            : { kind: 'Image', imageUrl: block.imageUrl }
        )
      }
    }

    if (blocks.length === 0) {
      setFormError('Add at least one paragraph or image.')
      return
    }

    let poll: SaveCampaignMessageRequest['poll'] = null
    if (form.pollEnabled) {
      const closesAt = new Date(form.pollClosesAtLocal)
      if (form.pollQuestions.length < 1 || form.pollQuestions.length > 10 ||
          Number.isNaN(closesAt.getTime()) || closesAt <= new Date() ||
          form.pollQuestions.some((question) => !question.question.trim() ||
            question.options.length < 2 || question.options.length > 10 ||
            question.options.some((option) => !option.text.trim()))) {
        setFormError('Enter 1–10 questions with 2–10 options each, and a future poll closing time.')
        return
      }
      let branch: CampaignPollBranch | null = null
      if (form.pollBranch) {
        const sourceIndex = form.pollQuestions.findIndex((question) => question.id === form.pollBranch?.questionId)
        const source = form.pollQuestions[sourceIndex]
        const routes = form.pollBranch.routes
        const targets = routes.map((route) => route.followUpQuestionId).filter((id): id is string => typeof id === 'string')
        if (!source || source.allowsMultiple || sourceIndex === form.pollQuestions.length - 1 ||
            routes.length !== source.options.length || routes.some((route) => route.followUpQuestionId === undefined) ||
            new Set(routes.map((route) => route.optionId)).size !== source.options.length ||
            source.options.some((option) => !routes.some((route) => route.optionId === option.id)) ||
            targets.length === 0 || new Set(targets).size !== targets.length ||
            targets.length !== form.pollQuestions.length - sourceIndex - 1 ||
            form.pollQuestions.slice(sourceIndex + 1).some((question) => !targets.includes(question.id))) {
          setFormError('Choose End poll or a unique follow-up question for every answer.')
          return
        }
        branch = { questionId: source.id, routes: routes.map((route) => ({ optionId: route.optionId, followUpQuestionId: route.followUpQuestionId ?? null })) }
      }
      if (form.expiresAtLocal && new Date(form.expiresAtLocal) <= closesAt) {
        setFormError('Message expiry must be after the poll closes.')
        return
      }
      poll = {
        question: form.pollQuestions[0].question.trim(),
        allowsMultiple: form.pollQuestions[0].allowsMultiple,
        closesAtUtc: closesAt.toISOString(),
        options: form.pollQuestions[0].options.map((option) => ({ ...option, text: option.text.trim() })),
        ...(form.pollQuestions.length > 1 ? { questions: form.pollQuestions.map((question) => ({
          ...question, question: question.question.trim(),
          options: question.options.map((option) => ({ ...option, text: option.text.trim() }))
        })) } : {}),
        ...(branch ? { branch } : {})
      }
    }

    const content = blocks
      .filter((block) => block.kind === 'Paragraph')
      .map((block) => block.text ?? '')
      .join('\n\n')

    let expiresAtUtc: string | null = null
    if (form.expiresAtLocal.trim()) {
      const parsed = new Date(form.expiresAtLocal)
      if (Number.isNaN(parsed.getTime())) {
        setFormError('Expiry must be a valid date and time.')
        return
      }
      expiresAtUtc = parsed.toISOString()
    }

    const payload: SaveCampaignMessageRequest = {
      title,
      content,
      audienceCohort: form.audienceCohort,
      targetGroupId: form.audienceCohort === 'GroupMembers' ? form.targetGroupId : null,
      audienceWindowDays: form.audienceWindowDays,
      requireMarketingOptIn: form.requireMarketingOptIn,
      expiresAtUtc,
      blocks,
      ...(poll ? { poll } : {}),
      displayMode: poll ? 'Persistent' : form.displayMode,
      canDismiss: form.requiresAcknowledgement ? false : form.canDismiss,
      requiresAcknowledgement: poll ? false : form.requiresAcknowledgement
    }

    setSaving(true)
    setFormError(null)
    try {
      const saved = editingId
        ? await updateCampaignMessage(editingId, payload)
        : await createCampaignMessage(payload)
      setCampaigns((prev) => {
        const withoutSaved = prev.filter((campaign) => campaign.id !== saved.id)
        return [saved, ...withoutSaved]
      })
      addToast({
        title: editingId ? 'Campaign updated' : 'Campaign created',
        description: `"${saved.title}" is saved as a draft.`
      })
      resetForm()
    } catch (error) {
      console.error('Failed to save campaign message', error)
      setFormError(error instanceof Error ? error.message : 'Could not save the campaign message.')
      addToast({ title: 'Save failed', description: 'Could not save the campaign message.' })
    } finally {
      setSaving(false)
    }
  }

  const handleSend = async (campaign: CampaignMessage) => {
    const summary = `Send to ${describeAudience(campaign.audienceCohort, campaign.requireMarketingOptIn)}?`
    const confirmed = window.confirm(`${summary} This cannot be undone once delivery starts.`)
    if (!confirmed) return

    setActingId(campaign.id)
    try {
      const updated = await sendCampaignMessage(campaign.id, campaign.audienceCohort === 'All')
      setCampaigns((prev) => prev.map((item) => item.id === updated.id ? updated : item))
      addToast({ title: 'Send started', description: `"${updated.title}" is now sending.` })
    } catch (error) {
      console.error('Failed to send campaign message', error)
      addToast({ title: 'Error', description: 'Could not start sending the campaign.' })
    } finally {
      setActingId(null)
    }
  }

  const openScheduleDialog = (campaign: CampaignMessage) => {
    setSchedulingId(campaign.id)
    setScheduledForLocal(campaign.scheduledForUtc ? toLocalInputValue(campaign.scheduledForUtc) : getDefaultScheduleValue())
    setScheduleError(null)
  }

  const closeScheduleDialog = useCallback(() => {
    if (scheduling) return
    setSchedulingId(null)
    setScheduledForLocal('')
    setScheduleError(null)
  }, [scheduling])

  const handleScheduleSubmit = async () => {
    if (!schedulingId) return

    const trimmed = scheduledForLocal.trim()
    if (!trimmed || Number.isNaN(new Date(trimmed).getTime())) {
      setScheduleError('Choose a valid date and time.')
      return
    }

    const scheduledForUtc = new Date(trimmed).toISOString()
    const campaign = campaigns.find((item) => item.id === schedulingId)
    const confirmAllUsers = campaign?.audienceCohort === 'All'
    if (confirmAllUsers && !window.confirm('Schedule this campaign for all users?')) {
      return
    }

    setScheduling(true)
    setScheduleError(null)
    try {
      const updated = await scheduleCampaignMessage(schedulingId, scheduledForUtc, confirmAllUsers)
      setCampaigns((prev) => prev.map((item) => item.id === updated.id ? updated : item))
      addToast({ title: 'Campaign scheduled', description: `"${updated.title}" will send at ${formatDateTime(updated.scheduledForUtc)}.` })
      setSchedulingId(null)
      setScheduledForLocal('')
    } catch (error) {
      console.error('Failed to schedule campaign message', error)
      const description = extractErrorMessage(error, 'Could not schedule the campaign.')
      setScheduleError(description)
      addToast({ title: 'Schedule failed', description })
    } finally {
      setScheduling(false)
    }
  }

  const handleCancel = async (campaign: CampaignMessage) => {
    const confirmed = window.confirm(`Cancel "${campaign.title}"? Users will no longer see this message.`)
    if (!confirmed) return

    setActingId(campaign.id)
    try {
      const updated = await cancelCampaignMessage(campaign.id)
      setCampaigns((prev) => prev.map((item) => item.id === updated.id ? updated : item))
      addToast({ title: 'Campaign canceled', description: `"${updated.title}" was canceled.` })
    } catch (error) {
      console.error('Failed to cancel campaign message', error)
      addToast({ title: 'Error', description: 'Could not cancel the campaign.' })
    } finally {
      setActingId(null)
    }
  }

  const handleDuplicate = async (campaign: CampaignMessage) => {
    setActingId(campaign.id)
    try {
      const duplicate = await duplicateCampaignMessage(campaign.id)
      setCampaigns((prev) => [duplicate, ...prev])
      startEdit(duplicate)
      addToast({ title: 'Campaign duplicated', description: `"${duplicate.title}" is ready to edit.` })
    } catch (error) {
      console.error('Failed to duplicate campaign message', error)
      addToast({ title: 'Error', description: 'Could not duplicate the campaign.' })
    } finally {
      setActingId(null)
    }
  }

  if (!isEditor) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-card p-8 text-center">
        <p className="font-medium text-foreground">Campaign messages can only be managed by Admin or Editor accounts.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Campaign Messages</h1>
          <p className="text-sm text-muted-foreground">
            Draft announcements from text, image, and button blocks and deliver them to user cohorts in the mobile app.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/campaign-polls" className="inline-flex h-10 items-center rounded-md border border-input px-4 text-sm font-medium text-foreground hover:bg-accent">Poll results</Link>
          <Button type="button" variant="outline" onClick={() => void loadCampaigns()} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
            Refresh
          </Button>
          <Button type="button" onClick={resetForm}>
            <Plus className="h-4 w-4" />
            New Message
          </Button>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)]">
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Campaigns</h2>
            <p className="text-sm text-muted-foreground">{campaigns.length} campaigns</p>
          </div>

          {loading ? (
            <div className="flex justify-center rounded-lg border border-border py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : campaigns.length === 0 ? (
            <EmptyState
              title="No campaigns yet"
              description="Create a draft message and send it to a cohort of users."
              icon={Megaphone}
            />
          ) : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-1">
              {campaigns.map((campaign) => {
                const imageCount = countImages(campaign.blocks)
                return (
                <Card key={campaign.id}>
                  <CardContent className="space-y-3 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="truncate font-semibold text-foreground">{campaign.title}</h3>
                        <p className="text-xs text-muted-foreground">
                          {campaign.audienceCohort}
                          {campaign.requireMarketingOptIn ? ' · marketing opt-in' : ''}
                          {' · '}created by {campaign.createdBy || 'unknown'} {formatDateTime(campaign.createdAtUtc)}
                        </p>
                      </div>
                      <span className={cn(
                        'shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold',
                        STATUS_BADGE_CLASSES[campaign.status]
                      )}>
                        {displayStatus(campaign)}
                      </span>
                    </div>
                    <p className="line-clamp-2 text-sm text-muted-foreground">{campaign.content}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {campaign.scheduledForUtc ? <span>Scheduled {formatDateTime(campaign.scheduledForUtc)}</span> : null}
                      <span>Sent {formatDateTime(campaign.sentAtUtc)}</span>
                      <span>Delivered {campaign.deliveredCount}/{campaign.targetedCount}</span>
                      <span>Shown {campaign.shownCount ?? 0}</span>
                      <span>Dismissed {campaign.dismissedCount ?? 0}</span>
                      <span>Acknowledged {campaign.acknowledgedCount ?? 0}</span>
                      <span>CTA {campaign.ctaClickCount ?? 0} ({((campaign.ctaClickThroughRate ?? 0) * 100).toFixed(1)}%)</span>
                      {imageCount > 0 ? <span>{imageCount} {imageCount === 1 ? 'image' : 'images'}</span> : null}
                      {campaign.expiresAtUtc ? <span>Expires {formatDateTime(campaign.expiresAtUtc)}</span> : null}
                      {campaign.poll ? <span>Poll closes {formatDateTime(campaign.poll.closesAtUtc)}</span> : null}
                    </div>
                    {campaign.auditHistory?.length ? (
                      <details className="text-xs text-muted-foreground">
                        <summary className="cursor-pointer font-medium">History ({campaign.auditHistory.length})</summary>
                        <ol className="mt-2 space-y-1 border-l border-border pl-3">
                          {[...campaign.auditHistory].reverse().map((entry, index) => (
                            <li key={`${entry.atUtc}-${index}`}>
                              <span className="font-medium text-foreground">{entry.action}</span> by {entry.actor} · {formatDateTime(entry.atUtc)}
                              {entry.detail ? <span> · {entry.detail}</span> : null}
                            </li>
                          ))}
                        </ol>
                      </details>
                    ) : null}
                    <div className="flex flex-wrap gap-2">
                      {campaign.poll ? <Link to={`/campaign-polls?campaign=${encodeURIComponent(campaign.id)}`} className="inline-flex h-8 items-center rounded-md border border-input px-3 text-sm font-medium text-foreground hover:bg-accent">View poll results</Link> : null}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={campaign.status !== 'Draft' || actingId === campaign.id}
                        onClick={() => startEdit(campaign)}
                      >
                        <Pencil className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={actingId === campaign.id}
                        onClick={() => void handleDuplicate(campaign)}
                      >
                        <Copy className="h-4 w-4" />
                        Duplicate
                      </Button>
                      {campaign.status === 'Draft' || campaign.status === 'Scheduled' ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={actingId === campaign.id}
                          onClick={() => openScheduleDialog(campaign)}
                        >
                          <CalendarClock className="h-4 w-4" />
                          {campaign.status === 'Scheduled' ? 'Reschedule' : 'Schedule'}
                        </Button>
                      ) : null}
                      {campaign.status === 'Draft' ? (
                        <Button
                          type="button"
                          size="sm"
                          disabled={actingId === campaign.id}
                          onClick={() => void handleSend(campaign)}
                        >
                          {actingId === campaign.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                          Send now
                        </Button>
                      ) : null}
                      {campaign.status === 'Scheduled' || campaign.status === 'Draft' || campaign.status === 'Sending' || campaign.status === 'Sent' ? (
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          disabled={actingId === campaign.id}
                          onClick={() => void handleCancel(campaign)}
                        >
                          <Ban className="h-4 w-4" />
                          {campaign.status === 'Scheduled' ? 'Cancel schedule' : campaign.status === 'Sent' ? 'Disable live' : 'Cancel'}
                        </Button>
                      ) : null}
                    </div>
                  </CardContent>
                </Card>
                )
              })}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle>{editingId ? 'Edit Campaign' : 'New Campaign'}</CardTitle>
              <CardDescription>Only draft campaigns can be edited or sent.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {formError ? (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {formError}
                </div>
              ) : null}

              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="campaign-title">Title</label>
                <Input
                  id="campaign-title"
                  value={form.title}
                  onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                  placeholder="Trail season kickoff"
                />
              </div>

              <div className="space-y-3 rounded-lg border border-border p-4">
                <label className="flex items-center gap-2 text-sm font-semibold">
                  <input type="checkbox" checked={form.pollEnabled} onChange={(event) => setForm((prev) => ({
                    ...prev,
                    pollEnabled: event.target.checked,
                    pollQuestions: event.target.checked && prev.pollQuestions.length === 0 ? [newPollQuestion()] : prev.pollQuestions,
                    displayMode: event.target.checked ? 'Persistent' : prev.displayMode,
                    requiresAcknowledgement: event.target.checked ? false : prev.requiresAcknowledgement
                  }))} />
                  Add a poll
                </label>
                {form.pollEnabled ? (
                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground">Question pages appear only in app versions that support them. People on older versions can see the poll after updating, if it is still available. Delivery counts include those recipients even before they update.</p>
                    <div className="space-y-1">
                      <label htmlFor="campaign-poll-closes" className="text-sm font-medium">Poll closes at</label>
                      <Input id="campaign-poll-closes" type="datetime-local" value={form.pollClosesAtLocal} onChange={(event) => setForm((prev) => ({ ...prev, pollClosesAtLocal: event.target.value }))} />
                      <p className="text-xs text-muted-foreground">Opens when delivery starts. Closed polls remain visible until the message expires or is disabled.</p>
                    </div>
                    {form.pollQuestions.slice(0, branchSourceIndex >= 0 ? branchSourceIndex + 1 : undefined).map((question, questionIndex) => renderPollQuestionEditor(question, questionIndex))}
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="outline" size="sm" disabled={form.pollQuestions.length >= 10} onClick={() => setForm((prev) => {
                        const next = [...prev.pollQuestions]
                        const sourceIndex = prev.pollBranch ? next.findIndex((question) => question.id === prev.pollBranch?.questionId) : -1
                        if (sourceIndex >= 0) next.splice(sourceIndex, 0, newPollQuestion())
                        else next.push(newPollQuestion())
                        return { ...prev, pollQuestions: next }
                      })}>{form.pollBranch ? 'Add shared question before branch' : 'Add question page'}</Button>
                      {form.pollBranch ? (
                        <Button type="button" variant="outline" size="sm" onClick={() => {
                          const hasFollowUps = form.pollBranch?.routes.some((route) => route.followUpQuestionId)
                          if (hasFollowUps && !window.confirm('Remove branching and its follow-up questions?')) return
                          setForm((prev) => ({ ...prev,
                            pollQuestions: prev.pollQuestions.filter((question) => !prev.pollBranch?.routes.some((route) => route.followUpQuestionId === question.id)),
                            pollBranch: null
                          }))
                          setPreviewBranchOptionId('')
                        }}>Remove branching</Button>
                      ) : form.pollQuestions.length > 0 ? (
                        <Button type="button" variant="outline" size="sm" disabled={form.pollQuestions.length >= 10 || form.pollQuestions[form.pollQuestions.length - 1].allowsMultiple} onClick={() => setForm((prev) => {
                          const source = prev.pollQuestions[prev.pollQuestions.length - 1]
                          return { ...prev, pollBranch: { questionId: source.id, routes: source.options.map((option) => ({ optionId: option.id, followUpQuestionId: undefined })) } }
                        })}>Branch by answer on last question</Button>
                      ) : null}
                    </div>
                    {form.pollBranch ? <p className="text-xs text-muted-foreground">Each answer must end the poll or lead to one follow-up. The follow-up ends the poll. Branching requires a single-choice question.</p> : null}
                  </div>
                ) : null}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Content</span>
                  <span className="text-xs text-muted-foreground">{form.blocks.length}/{MAX_BLOCKS} blocks</span>
                </div>
                <div className="space-y-3">
                  {form.blocks.map((block, index) => (
                    <div key={index} className="space-y-2 rounded-lg border border-border p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {block.kind}
                        </span>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            aria-label={`Move block ${index + 1} up`}
                            disabled={index === 0 || saving}
                            onClick={() => moveBlock(index, -1)}
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            aria-label={`Move block ${index + 1} down`}
                            disabled={index === form.blocks.length - 1 || saving}
                            onClick={() => moveBlock(index, 1)}
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            aria-label={`Remove block ${index + 1}`}
                            disabled={saving}
                            onClick={() => removeBlock(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {block.kind === 'Paragraph' ? (
                        <Textarea
                          aria-label={`Paragraph ${index + 1}`}
                          value={block.text ?? ''}
                          onChange={(event) => updateBlock(index, { text: event.target.value })}
                          rows={4}
                          placeholder="Plain text only."
                        />
                      ) : block.kind === 'Button' ? (
                        <ButtonBlockFields
                          index={index}
                          block={block}
                          targets={deepLinkTargets}
                          onChange={(patch) => updateBlock(index, patch)}
                        />
                      ) : (
                        <div className="flex items-center gap-3">
                          {block.imageUrl ? (
                            <button
                              type="button"
                              aria-label={`Choose media library graphic ${index + 1}`}
                              onClick={() => setPickerBlockIndex(index)}
                            >
                              <img
                                src={block.imageUrl}
                                alt={`Block ${index + 1} attachment`}
                                className="h-16 w-16 rounded-md border border-border object-cover"
                              />
                            </button>
                          ) : (
                            <div className="flex h-16 w-16 items-center justify-center rounded-md border border-dashed border-border text-muted-foreground">
                              <ImagePlus className="h-5 w-5" />
                            </div>
                          )}
                          <div className="space-y-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setPickerBlockIndex(index)}
                            >
                              <ImagePlus className="h-4 w-4" />
                              Upload image
                            </Button>
                            <Input
                              aria-label={`Alt text ${index + 1}`}
                              maxLength={200}
                              value={block.caption ?? ''}
                              onChange={(event) => updateBlock(index, { caption: event.target.value })}
                              placeholder="Optional alt text for screen readers"
                              className="max-w-xs"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={form.blocks.length >= MAX_BLOCKS || saving}
                    onClick={() => addBlock('Paragraph')}
                  >
                    <Plus className="h-4 w-4" />
                    Add paragraph
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={form.blocks.length >= MAX_BLOCKS || saving}
                    onClick={() => addBlock('Image')}
                  >
                    <ImagePlus className="h-4 w-4" />
                    Add image
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={form.blocks.length >= MAX_BLOCKS || hasButtonBlock || saving}
                    onClick={() => addBlock('Button')}
                  >
                    <MousePointerClick className="h-4 w-4" />
                    Add button
                  </Button>
                </div>
                {hasButtonBlock ? (
                  <p className="text-xs text-muted-foreground">
                    Only the first button becomes the primary action of the message.
                  </p>
                ) : null}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="campaign-cohort">Audience</label>
                  <select
                    id="campaign-cohort"
                    value={form.audienceCohort}
                    onChange={(event) => setForm((prev) => ({ ...prev, audienceCohort: event.target.value as CampaignAudienceCohort }))}
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    {COHORT_OPTIONS.map((cohort) => (
                      <option key={cohort.value} value={cohort.value}>{cohort.label}</option>
                    ))}
                  </select>
                  {form.audienceCohort === 'GroupMembers' ? (
                    <select
                      aria-label="Target group"
                      value={form.targetGroupId}
                      onChange={(event) => setForm((prev) => ({ ...prev, targetGroupId: event.target.value }))}
                      className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    >
                      <option value="">Choose a group…</option>
                      {audienceGroups.map((group) => (
                        <option key={group.id} value={group.id}>{group.name} ({group.memberCount})</option>
                      ))}
                    </select>
                  ) : null}
                  {form.audienceCohort === 'NewUsers' || form.audienceCohort === 'ActiveUsers' ? (
                    <Input
                      aria-label="Audience window days"
                      type="number"
                      min={1}
                      max={365}
                      value={form.audienceWindowDays}
                      onChange={(event) => setForm((prev) => ({ ...prev, audienceWindowDays: Number(event.target.value) }))}
                    />
                  ) : null}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="campaign-expires-at">Expires at</label>
                  <Input
                    id="campaign-expires-at"
                    type="datetime-local"
                    value={form.expiresAtLocal}
                    onChange={(event) => setForm((prev) => ({ ...prev, expiresAtLocal: event.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">Optional. Users no longer see the message after this time.</p>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={form.requireMarketingOptIn}
                  onChange={(event) => setForm((prev) => ({ ...prev, requireMarketingOptIn: event.target.checked }))}
                  className="h-4 w-4 rounded border-input"
                />
                Require marketing opt-in
              </label>
              <p className="-mt-3 text-xs text-muted-foreground">
                Audience: {describeAudience(form.audienceCohort, form.requireMarketingOptIn)}.
              </p>

              <div className="space-y-3 rounded-lg border border-border p-4">
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  <span className="text-sm font-semibold">Mobile preview</span>
                </div>
                {form.pollEnabled && form.pollBranch && branchSource ? (
                  <div className="space-y-1">
                    <label htmlFor="campaign-poll-preview-path" className="text-sm font-medium">Preview answer path</label>
                    <select id="campaign-poll-preview-path" value={previewBranchOptionId} onChange={(event) => setPreviewBranchOptionId(event.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                      <option value="">Choose an answer</option>
                      {branchSource.options.map((option, index) => <option key={option.id} value={option.id}>{option.text.trim() || `Option ${index + 1}`}</option>)}
                    </select>
                  </div>
                ) : null}
                <div data-testid="campaign-mobile-preview" className="mx-auto max-w-sm rounded-2xl border border-border bg-background p-4 shadow-sm">
                  <p className="font-semibold text-foreground">{form.title || 'Campaign title'}</p>
                  <div className="mt-2 space-y-3 text-sm text-muted-foreground">
                    {form.blocks.map((block, index) => block.kind === 'Paragraph' ? (
                      <p key={index} className="whitespace-pre-wrap">{block.text || 'Message copy'}</p>
                    ) : block.kind === 'Image' && block.imageUrl ? (
                      <img key={index} src={block.imageUrl} alt={block.caption ?? ''} className="max-h-48 w-full rounded-lg object-cover" />
                    ) : block.kind === 'Button' ? (
                      <span key={index} className="inline-flex rounded-full bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground">
                        {block.label || 'Call to action'}
                      </span>
                    ) : null)}
                  </div>
                  {form.pollEnabled ? <div className="mt-4 space-y-3 border-t border-border pt-3 text-sm">{previewQuestions.map((question, index) => <div key={question.id} className="space-y-2"><p className="font-semibold">Page {index + 1}: {question.question || 'Poll question'}</p>{question.options.map((option) => <p key={option.id} className="rounded-md border border-border px-3 py-2">○ {option.text || 'Answer option'}</p>)}</div>)}{previewRoute?.followUpQuestionId === null ? <p>Poll ends after this answer.</p> : null}</div> : null}
                </div>
              </div>

              <div className="space-y-3 rounded-lg border border-border p-4">
                <p className="text-sm font-semibold">Display rules</p>
                <select
                  aria-label="Display mode"
                  value={form.displayMode}
                  disabled={form.pollEnabled}
                  onChange={(event) => setForm((prev) => ({ ...prev, displayMode: event.target.value as CampaignDisplayMode }))}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="Persistent">Keep showing until dismissed or expired</option>
                  <option value="Once">Show once per user</option>
                </select>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.canDismiss}
                    disabled={form.requiresAcknowledgement}
                    onChange={(event) => setForm((prev) => ({ ...prev, canDismiss: event.target.checked }))}
                  />
                  User can dismiss
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                  checked={form.requiresAcknowledgement}
                  disabled={form.pollEnabled}
                    onChange={(event) => setForm((prev) => ({
                      ...prev,
                      requiresAcknowledgement: event.target.checked,
                      canDismiss: event.target.checked ? false : prev.canDismiss
                    }))}
                  />
                  Require acknowledgment or CTA action before closing
                </label>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm} disabled={saving}>
                  Reset
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {editingId ? 'Save Changes' : 'Create Draft'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>

      <Dialog open={schedulingId !== null} onOpenChange={(open) => { if (!open) closeScheduleDialog() }}>
        <DialogContent className="w-[min(96vw,440px)] p-6">
          <DialogTitle>Schedule Campaign</DialogTitle>
          <DialogDescription>
            Pick when the campaign should be delivered. It must be in the future.
          </DialogDescription>
          <form
            className="mt-4 space-y-4"
            onSubmit={(event) => {
              event.preventDefault()
              void handleScheduleSubmit()
            }}
          >
            {scheduleError ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {scheduleError}
              </div>
            ) : null}
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="campaign-scheduled-for">Scheduled for</label>
              <Input
                id="campaign-scheduled-for"
                type="datetime-local"
                value={scheduledForLocal}
                onChange={(event) => setScheduledForLocal(event.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={closeScheduleDialog} disabled={scheduling}>
                Cancel
              </Button>
              <Button type="submit" disabled={scheduling}>
                {scheduling ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
                Schedule
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <MediaPickerDialog
        open={pickerBlockIndex !== null}
        onOpenChange={(open) => { if (!open) setPickerBlockIndex(null) }}
        onSelect={(asset) => {
          if (pickerBlockIndex !== null) handleMediaAssetSelected(pickerBlockIndex, asset)
        }}
      />
    </div>
  )
}

const getDefaultScheduleValue = () => {
  const tomorrowAtNine = new Date()
  tomorrowAtNine.setDate(tomorrowAtNine.getDate() + 1)
  tomorrowAtNine.setHours(9, 0, 0, 0)
  return toLocalInputValue(tomorrowAtNine.toISOString())
}

const extractErrorMessage = (error: unknown, fallback: string) => {
  const data = (error as { response?: { data?: { detail?: string; message?: string } } })?.response?.data
  return data?.detail ?? data?.message ?? fallback
}

const toLocalInputValue = (isoValue: string) => {
  const parsed = new Date(isoValue)
  if (Number.isNaN(parsed.getTime())) return ''
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`
}
